import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import CodeBlock from './CodeBlock';

const QuickStart = () => {
  const [currentStep, setCurrentStep] = useState(0);

  const steps = [
    {
      id: 'signup',
      title: 'Create Account',
      description: 'Sign up for a MessagingHub Pro account to get your API credentials.',
      icon: 'UserPlus',
      content: (
        <div className="space-y-4">
          <p className="text-muted-foreground">
            Get started by creating your free developer account. You'll receive 1,000 free messages per month.
          </p>
          <Button variant="default" iconName="ExternalLink" iconPosition="right">
            Create Free Account
          </Button>
        </div>
      )
    },
    {
      id: 'api-key',
      title: 'Get API Key',
      description: 'Generate your API key from the developer dashboard.',
      icon: 'Key',
      content: (
        <div className="space-y-4">
          <p className="text-muted-foreground">
            Once logged in, navigate to your dashboard and generate your API key. Keep it secure!
          </p>
          <CodeBlock
            code={`// Your API key will look like this:
const API_KEY = 'mhp_live_1234567890abcdef';

// Set it in your environment variables
export MESSAGING_HUB_API_KEY=mhp_live_1234567890abcdef`}
            language="bash"
            title="API Key Setup"
            description="Set up your API key for authentication"
          />
        </div>
      )
    },
    {
      id: 'install',
      title: 'Install SDK',
      description: 'Install our SDK for your preferred programming language.',
      icon: 'Download',
      content: (
        <div className="space-y-4">
          <p className="text-muted-foreground">
            Choose your preferred language and install our official SDK:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <CodeBlock
              code="npm install @messaginghub/sdk"
              language="bash"
              title="Node.js / JavaScript"
              description="Install the JavaScript/TypeScript SDK"
            />
            <CodeBlock
              code="pip install messaging-core"
              language="bash"
              title="Python"
              description="Install the Python SDK"
            />
          </div>
        </div>
      )
    },
    {
      id: 'first-message',
      title: 'Send First Message',
      description: 'Send your first message using our API.',
      icon: 'Send',
      content: (
        <div className="space-y-4">
          <p className="text-muted-foreground">
            Here's how to send your first message:
          </p>
          <CodeBlock
            code={`import { MessagingHub } from '@messaginghub/sdk';

const client = new MessagingHub({
  apiKey: process.env.MESSAGING_HUB_API_KEY
});

async function sendMessage() {
  try {
    const result = await client.messages.send({
      to: '+1234567890',
      from: '+1987654321',
      body: 'Hello from MessagingHub Pro!'
    });
    
    console.log('Message sent:', result.messageId);
  } catch (error) {
    console.error('Error:', error.message);
  }
}

sendMessage();`}
            language="javascript"
            title="Send Your First Message"
            description="Complete example of sending a message"
          />
        </div>
      )
    }
  ];

  const nextStep = () => {
    if (currentStep < steps?.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  return (
    <div className="space-y-8">
      {/* Progress Bar */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-foreground">Quick Start Guide</h2>
          <span className="text-sm text-muted-foreground">
            Step {currentStep + 1} of {steps?.length}
          </span>
        </div>
        
        <div className="flex items-center space-x-2">
          {steps?.map((_, index) => (
            <div key={index} className="flex items-center">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium transition-colors ${
                  index <= currentStep
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground'
                }`}
              >
                {index + 1}
              </div>
              {index < steps?.length - 1 && (
                <div
                  className={`w-12 h-0.5 mx-2 transition-colors ${
                    index < currentStep ? 'bg-primary' : 'bg-muted'
                  }`}
                />
              )}
            </div>
          ))}
        </div>
      </div>
      {/* Current Step Content */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-start space-x-4 mb-6">
          <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name={steps?.[currentStep]?.icon} size={24} className="text-primary" />
          </div>
          <div>
            <h3 className="text-xl font-semibold text-foreground mb-2">
              {steps?.[currentStep]?.title}
            </h3>
            <p className="text-muted-foreground">
              {steps?.[currentStep]?.description}
            </p>
          </div>
        </div>

        <div className="mb-8">
          {steps?.[currentStep]?.content}
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            onClick={prevStep}
            disabled={currentStep === 0}
            iconName="ChevronLeft"
            iconPosition="left"
          >
            Previous
          </Button>
          
          <div className="flex items-center space-x-2">
            {currentStep === steps?.length - 1 ? (
              <Button variant="default" iconName="Rocket" iconPosition="right">
                Start Building
              </Button>
            ) : (
              <Button
                variant="default"
                onClick={nextStep}
                iconName="ChevronRight"
                iconPosition="right"
              >
                Next Step
              </Button>
            )}
          </div>
        </div>
      </div>
      {/* Additional Resources */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-3">
            <Icon name="Book" size={20} className="text-primary" />
            <h4 className="font-medium text-foreground">Documentation</h4>
          </div>
          <p className="text-sm text-muted-foreground mb-3">
            Comprehensive guides and API reference documentation.
          </p>
          <Button variant="ghost" size="sm" iconName="ArrowRight" iconPosition="right">
            View Docs
          </Button>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-3">
            <Icon name="Code" size={20} className="text-primary" />
            <h4 className="font-medium text-foreground">Code Examples</h4>
          </div>
          <p className="text-sm text-muted-foreground mb-3">
            Ready-to-use code examples in multiple programming languages.
          </p>
          <Button variant="ghost" size="sm" iconName="ArrowRight" iconPosition="right">
            Browse Examples
          </Button>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-3">
            <Icon name="MessageSquare" size={20} className="text-primary" />
            <h4 className="font-medium text-foreground">Support</h4>
          </div>
          <p className="text-sm text-muted-foreground mb-3">
            Get help from our developer support team.
          </p>
          <Button variant="ghost" size="sm" iconName="ArrowRight" iconPosition="right">
            Contact Support
          </Button>
        </div>
      </div>
    </div>
  );
};

export default QuickStart;